import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  ArticleMetadata,
  MintelTracking,
  TrackPresentModeFn,
  TrackPresentPanelDownloadFn,
  TrackRecommendedContentFn,
} from '/types';

import { AppShellContext, MetadataContext } from './contexts';
import useExperiments from './useExperiments';

type UseTrackingFn = () => {
  trackArticleView: () => void;
  trackPresentMode: TrackPresentModeFn;
  trackRecommendedContent: TrackRecommendedContentFn;
  trackScroll: (visibleBlock: number) => void;
  trackVideo: MintelTracking['video'];
  trackOpeningInPageNav: () => void;
  trackSwitchingTabsInInPageNav: () => void;
  trackClosingInPageNav: () => void;
  trackDatabookLink: (url: string) => void;
  trackContentNavigate: (
    title: string,
    id: string,
    text: string,
    parentText?: string,
  ) => void;
  trackFavorite: (tag: string) => void;
  trackAccessPanel: () => void;
  trackLongTermPreview: (action: string) => void;
  trackPresentPanelDownload: TrackPresentPanelDownloadFn;
};

// Send a page scroll event when reaching these points.
const SCROLL_PERCENTAGE_STEPS = [25, 50, 75, 100];

export const getContentTrackingPropertiesFromMetadata = (
  metadata: ArticleMetadata,
): Record<string, unknown> => ({
  Title: metadata.title,
  'Content Hub': metadata.site,
  'Content Product': metadata.platform,
  Tags: metadata.tags,
  Author: metadata.editor.name || 'Authorless content',
  'Display Date': metadata.date.display,
  'Content Type': metadata.aggregated_insight_type,
  'Content Group Identifier': metadata.group_id,
  'Content Group Display Name': metadata.group_title,
  'Content Unique Identifier': metadata.id,
  Slug: metadata.slug || 'Slugless content',
  'Content Language': metadata.language,
  'Primary Library': metadata.primary_subscription?.title,
  'Secondary Libraries': metadata.secondary_subscriptions?.map(
    (sub) => sub.title,
  ),
});

const useTracking: UseTrackingFn = () => {
  const { startTracking } = useContext(AppShellContext);
  const metadata = useContext(MetadataContext);
  const experiments = useExperiments();
  const [trackArticleViewPending, setTrackArticleViewPending] = useState(false);

  const mintelTracking = useMemo(
    () =>
      startTracking
        ? startTracking('Clients', BUILD_TARGET === 'live', true)
        : null,
    [startTracking],
  );

  // Unlike the rest of the tracking functions trackArticleView doesn't immediately
  // fire a mixpanel event, because some dependencies might still be missing (they
  // are going to load soon in asynchronous useEffects). Instead, we log the request
  // to send this event as pending and send it when all dependencies are ready.
  const trackArticleView = (): void => setTrackArticleViewPending(true);

  useEffect(() => {
    if (mintelTracking && metadata && experiments && trackArticleViewPending) {
      mintelTracking.view('Content', {
        ...getContentTrackingPropertiesFromMetadata(metadata),
        Experiments: experiments,
      });
      setTrackArticleViewPending(false);
    }
  }, [mintelTracking, metadata, experiments, trackArticleViewPending]);

  const trackRecommendedContent = useCallback(
    (relatedTitle: string) => {
      mintelTracking &&
        metadata &&
        mintelTracking.engage('Content', {
          ...getContentTrackingPropertiesFromMetadata(metadata),
          'Related Title': relatedTitle,
          Action: 'Moved to Related Content',
          Experiments: experiments,
        });
    },
    [mintelTracking, metadata, experiments],
  );

  const [
    previousScrollPercentageStep,
    setPreviousScrollPercentageStep,
  ] = useState(0);

  const trackScroll = useCallback(
    (scrollPercentage) => {
      if (
        mintelTracking &&
        metadata &&
        scrollPercentage > previousScrollPercentageStep
      ) {
        SCROLL_PERCENTAGE_STEPS.filter(
          (x) => x > previousScrollPercentageStep && x <= scrollPercentage,
        ).forEach((x) => {
          mintelTracking.engage('Content', {
            ...getContentTrackingPropertiesFromMetadata(metadata),
            'Scroll Distance': `${x}%`,
            Action: 'Scroll',
            Experiments: experiments,
          });
          setPreviousScrollPercentageStep(x);
        });
      }
    },
    [
      mintelTracking,
      metadata,
      previousScrollPercentageStep,
      setPreviousScrollPercentageStep,
      experiments,
    ],
  );

  const trackVideo = useCallback(
    (eventName, properties) => {
      mintelTracking &&
        mintelTracking.video(eventName, {
          ...properties,
          Experiments: experiments,
        });
    },
    [mintelTracking, experiments],
  );

  const trackPresentMode = useCallback(
    (fulltext, user, width, height) => {
      mintelTracking &&
        metadata &&
        mintelTracking.engage('Presentation Mode', {
          ...getContentTrackingPropertiesFromMetadata(metadata),
          User: user,
          'Screen Resolution': `${width}x${height}`,
          'Present Type': fulltext ? 'Full Text' : 'Keynote',
          Experiments: experiments,
        });
    },
    [mintelTracking, metadata, experiments],
  );

  const trackOpeningInPageNav = useCallback(() => {
    mintelTracking?.view('Support', {
      Title: 'Opening Content Navigator',
      Experiments: experiments,
    });
  }, [mintelTracking, experiments]);

  const trackSwitchingTabsInInPageNav = useCallback(() => {
    mintelTracking?.view('Support', {
      Title: 'Switching Tabs in the Content Navigator',
      Experiments: experiments,
    });
  }, [mintelTracking, experiments]);

  const trackClosingInPageNav = useCallback(() => {
    mintelTracking?.view('Support', {
      Title: 'Closing Content Navigator',
      Experiments: experiments,
    });
  }, [mintelTracking, experiments]);

  const trackDatabookLink = useCallback(
    (url) => {
      mintelTracking &&
        mintelTracking.navigate({
          Action: 'Exit to Other Mintel Website',
          'Link Location': 'Content Piece - Navigator',
          'Destination Product Name': 'Consumer Data',
          url,
          Experiments: experiments,
        });
    },
    [mintelTracking, experiments],
  );

  const trackContentNavigate = useCallback(
    (title, id, text, parentText) => {
      let linkLocation = 'Content Navigator - Section: ';
      if (parentText) {
        linkLocation += `${parentText} - `;
      }
      linkLocation += text;

      mintelTracking &&
        mintelTracking.navigate({
          Action: 'Click Content Navigator',
          'Link Location': linkLocation,
          'Destination Product Name': 'Clients',
          Title: title,
          url: `${window.location.href}#${id}`,
          Experiments: experiments,
        });
    },
    [mintelTracking, experiments],
  );

  const trackFavorite = useCallback(
    (tag) => {
      mintelTracking &&
        metadata &&
        mintelTracking.engage('Content', {
          ...getContentTrackingPropertiesFromMetadata(metadata),
          'Favourite List Name': tag,
          Action: 'Favourite',
          Experiments: experiments,
        });
    },
    [mintelTracking, metadata, experiments],
  );

  const trackAccessPanel = useCallback(() => {
    mintelTracking &&
      metadata &&
      mintelTracking.view('Sidebar', {
        ...getContentTrackingPropertiesFromMetadata(metadata),
        Panel: 'Access',
        Experiments: experiments,
      });
  }, [mintelTracking, metadata, experiments]);

  const trackLongTermPreview = useCallback(
    (action) => {
      mintelTracking &&
        metadata &&
        mintelTracking.engage('Sidebar', {
          ...getContentTrackingPropertiesFromMetadata(metadata),
          Panel: 'Long Term Preview',
          Action: action,
          Experiments: experiments,
        });
    },
    [mintelTracking, metadata, experiments],
  );

  const trackPresentPanelDownload = useCallback(() => {
    mintelTracking &&
      metadata &&
      mintelTracking.download('Sidebar', {
        ...getContentTrackingPropertiesFromMetadata(metadata),
        Panel: 'Present',
        Action: 'Download PPT',
        Format: '.ppt',
        Experiments: experiments,
      });
  }, [mintelTracking, metadata, experiments]);

  return {
    trackArticleView,
    trackContentNavigate,
    trackDatabookLink,
    trackOpeningInPageNav,
    trackSwitchingTabsInInPageNav,
    trackClosingInPageNav,
    trackPresentMode,
    trackRecommendedContent,
    trackScroll,
    trackVideo,
    trackFavorite,
    trackLongTermPreview,
    trackAccessPanel,
    trackPresentPanelDownload,
  };
};

export default useTracking;
